/**
 * `<EmailVerificationGate>` — blocks the post-signup app shell until the user's
 * Auth0 `email_verified` claim is true (ENG-3797).
 *
 * Inserted ONCE inside `ProfileSynchronizer`, wrapping `<Network>` in
 * router.tsx (spec-fragment.md §1). Both the browser and Electron `Protect`
 * branches render the same gated Outlet, so this single insertion covers both
 * surfaces — no separate Electron gate is needed.
 *
 * Fail-CLOSED on unknown and fail-OPEN on flag-off (spec §4/§6): a user is
 * never silently allowed when verification is unconfirmed, but the gate is
 * fully transparent whenever the flag is off, loading, or errored.
 */

import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Loader } from '@studio/design-system/loader';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { toIdFromHex } from '#~/api/utils.js';
import { track } from '#~/analytics/track.js';
import { EVENT_CATEGORIES } from '#~/analytics/constants/event-categories.js';
import { EVENT_NAMES } from '#~/analytics/constants/event-names.js';
import {
  changeUserEmail,
  getVerificationEmailSentFor,
  resendVerificationEmail
} from '#~/auth/utils.js';
import {
  EMAIL_VERIFICATION_MAX_CONSECUTIVE_POLL_FAILURES,
  EMAIL_VERIFICATION_POLL_INTERVAL_MS,
  EMAIL_VERIFICATION_RESEND_COOLDOWN_MS,
  EMAIL_VERIFICATION_RESOLVE_TIMEOUT_MS
} from './constants.js';
import { classifyRefreshFailure, decideGate, remainingCooldownMs, shouldGate } from './logic.js';
import { useVerificationSignal } from './use-verification-signal.js';
import { PendingVerification, VerificationError } from './pending-verification.jsx';

/** Coarse, PII-free reason for a failed poll (spec §analytics privacy). */
const POLL_FAILURE_REASON = 'refresh_failed';

// WHY there is no forced-re-auth escalation here (ENG-3797 auth fix): the gate
// must NEVER bounce a pending user to the Auth0 login screen while waiting for
// verification. The auto-dismiss poll now reads Auth0's live /userinfo (see
// use-verification-signal.refresh), which flips email_verified to true the
// moment the user verifies — so a forced re-login is never needed and the
// previous loginWithRedirect({prompt:'login'}) escalation (the confirmed
// live-test bug) and its sessionStorage loop-guard were removed entirely.

/** `<EmailVerificationGate>` component. */
export const EmailVerificationGate = ({ children }: { children: ReactNode }): ReactElement => {
  // defaultValue:false ⇒ loading/error resolves to OFF (spec §6 passthrough).
  const { enabled } = useFeatureFlag(PostHogFeatureFlag.EMAIL_VERIFICATION_GATE, {
    defaultValue: false
  });

  const { claims, signal, surface, refresh } = useVerificationSignal();
  const { getAccessTokenSilently, logout } = useAuth0();
  const studioUser = useDepot((µ) => µ.user);

  const gated = shouldGate(enabled);

  const [pollFailed, setPollFailed] = useState(false);
  const [resolveExhausted, setResolveExhausted] = useState(false);
  // Arm the cooldown if the auto-send already fired for this email this session
  // (spec §7) so the user can't immediately trigger a second send.
  const [sentAt, setSentAt] = useState<number | undefined>(() =>
    getVerificationEmailSentFor() !== undefined && getVerificationEmailSentFor() === claims.email
      ? Date.now()
      : undefined
  );
  const [now, setNow] = useState(() => Date.now());

  // Server-derived gate signal (ENG-4602). Tri-state: true gates, false/undefined
  // fail open. decideGate consults it before the claim signal.
  const verificationRequired = studioUser?.verificationRequired ?? undefined;

  const outcome = decideGate({
    flagEnabled: enabled,
    signal,
    verificationRequired,
    resolveExhausted,
    pollFailed
  });
  const cooldownRemaining = remainingCooldownMs(sentAt, now, EMAIL_VERIFICATION_RESEND_COOLDOWN_MS);

  // Fire-once guards for the funnel events.
  const shownFiredRef = useRef(false);
  const unknownFiredRef = useRef(false);
  const prevSignalRef = useRef(signal);
  // Fire-once guard for the grandfather-exempt event (ENG-4602).
  const grandfatheredFiredRef = useRef(false);

  // Count CONSECUTIVE genuine connection failures (ENG-3797 Failure 1). Reset on
  // any successful poll / verified signal / manual retry. The terminal error
  // screen only appears once this reaches
  // EMAIL_VERIFICATION_MAX_CONSECUTIVE_POLL_FAILURES — a single ordinary
  // silent-auth rejection (login_required/consent_required/timeout) no longer
  // flips the gate to "Check your connection" before the user verifies.
  const consecutiveConnectionFailuresRef = useRef(0);

  // VERIFICATION_GATE_SHOWN — fired each time the pending UI appears (spec
  // §analytics). Reset when the gate leaves the pending state so a later
  // re-block fires again.
  useEffect(() => {
    if (gated && outcome === 'pending' && !shownFiredRef.current) {
      shownFiredRef.current = true;
      void track({
        category: EVENT_CATEGORIES.ONBOARDING,
        name: EVENT_NAMES.VERIFICATION_GATE_SHOWN,
        properties: { surface, flag_value: enabled === true, decision_reason: 'unverified' }
      });
    }

    if (outcome !== 'pending') {
      shownFiredRef.current = false;
    }
  }, [gated, outcome, surface, enabled]);

  // VERIFICATION_GATE_GRANDFATHERED — the gate allowed a NON-verified account
  // only because the server marked it grandfathered (verificationRequired ===
  // false with an unverified/unknown claim). Fires once so rollout can watch the
  // exempted legacy population (ENG-4602). The ordinary verified-allow path
  // (signal === 'verified') is excluded, and an undefined signal (absent field /
  // loading) never fires — only an explicit server false.
  useEffect(() => {
    if (
      gated &&
      outcome === 'allow' &&
      verificationRequired === false &&
      signal !== 'verified' &&
      !grandfatheredFiredRef.current
    ) {
      grandfatheredFiredRef.current = true;
      void track({
        category: EVENT_CATEGORIES.ONBOARDING,
        name: EVENT_NAMES.VERIFICATION_GATE_GRANDFATHERED,
        properties: { surface, flag_value: enabled === true }
      });
    }
  }, [gated, outcome, verificationRequired, signal, surface, enabled]);

  // VERIFICATION_COMPLETED — signal transitions to verified while gated (the
  // auto-dismiss success path, spec §analytics).
  useEffect(() => {
    if (gated && prevSignalRef.current !== 'verified' && signal === 'verified') {
      void track({
        category: EVENT_CATEGORIES.ONBOARDING,
        name: EVENT_NAMES.VERIFICATION_COMPLETED,
        properties: { surface }
      });
    }
    prevSignalRef.current = signal;
  }, [gated, signal, surface]);

  // VERIFICATION_STATUS_UNKNOWN — terminal unknown reached (spec §4).
  useEffect(() => {
    if (gated && resolveExhausted && !pollFailed && !unknownFiredRef.current) {
      unknownFiredRef.current = true;
      void track({
        category: EVENT_CATEGORIES.ONBOARDING,
        name: EVENT_NAMES.VERIFICATION_STATUS_UNKNOWN,
        properties: { surface }
      });
    }
  }, [gated, resolveExhausted, pollFailed, surface]);

  // Bounded resolving window: if the signal is still unknown after the bound,
  // go terminal-error rather than spinning forever (spec §4, fail-closed).
  useEffect(() => {
    if (!gated || signal !== 'unknown' || resolveExhausted || pollFailed) {
      return;
    }
    const timer = setTimeout(
      () => setResolveExhausted(true),
      EMAIL_VERIFICATION_RESOLVE_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [gated, signal, resolveExhausted, pollFailed]);

  // A verified signal clears the connection-failure run AND the terminal poll-
  // failure / resolve-exhausted flags. decideGate now allows on
  // signal==='verified' BEFORE it checks pollFailed (logic.ts, ENG-3797 flicker
  // fix), so a verified user is already let in even while these flags are set —
  // this reset is the belt-and-braces companion: it zeroes the counters so a
  // LATER legitimate re-block (e.g. a different unverified identity in the same
  // mount) starts from a clean slate rather than an inherited failure run (codex
  // review b1).
  useEffect(() => {
    if (signal === 'verified') {
      consecutiveConnectionFailuresRef.current = 0;
      setPollFailed(false);
      setResolveExhausted(false);
    }
  }, [signal]);

  // Auto-dismiss / resolve poll: re-read the LIVE verification state (Auth0
  // /userinfo, via refresh()) while blocking or resolving, so a user who clicks
  // the verify link is let into the app WITHOUT any action and WITHOUT a re-login
  // (ENG-3797 spec §3 Path 1). /userinfo reflects Auth0's current record and
  // flips email_verified to true the moment they verify, so there is no stale-
  // token dead-end and no forced re-auth — the gate simply transitions to
  // 'allow'. A genuine SUSTAINED connection failure is still terminal (spec §4)
  // and surfaces VERIFICATION_POLL_FAILED, but only after a bounded run.
  useEffect(() => {
    if (!gated || (outcome !== 'pending' && outcome !== 'resolving')) {
      return;
    }
    // Guard against an in-flight poll resolving AFTER this effect tears down
    // (codex round-4 race): clearInterval stops future ticks but a refresh()
    // promise already in flight can still settle. Without this flag its handler
    // would mutate state for a stale effect run — e.g. set pollFailed after the
    // gate already allowed a verified user, which a LATER same-mount switch to a
    // different/changed-email unverified identity could then inherit and jump
    // straight to the error screen. The flag makes every settled handler a no-op
    // once the effect has been cleaned up.
    let cancelled = false;
    // Surface the terminal connection-error screen + analytics. Called ONLY once
    // a sustained run of genuine connection failures is confirmed (Failure 1).
    const recordPollFailure = (): void => {
      setPollFailed(true);
      void track({
        category: EVENT_CATEGORIES.ONBOARDING,
        name: EVENT_NAMES.VERIFICATION_POLL_FAILED,
        properties: { surface, reason: POLL_FAILURE_REASON }
      });
    };
    // Per ENG-3797 Failure 1: classify a rejected poll. A recoverable outcome
    // (login_required/consent_required/transient timeout while obtaining the
    // bearer token) is EXPECTED while still unverified — keep pending and keep
    // polling, do not count it. Only consecutive genuine connection failures
    // reach the error screen.
    const handlePollRejection = (error: unknown): void => {
      if (cancelled || classifyRefreshFailure(error) === 'recoverable') {
        return;
      }
      consecutiveConnectionFailuresRef.current += 1;
      if (
        consecutiveConnectionFailuresRef.current >= EMAIL_VERIFICATION_MAX_CONSECUTIVE_POLL_FAILURES
      ) {
        recordPollFailure();
      }
    };
    const interval = setInterval(() => {
      void refresh()
        .then(() => {
          if (cancelled) {
            return;
          }
          // A successful poll clears any partial run of genuine failures so
          // transient blips never accumulate to an error (Failure 1).
          consecutiveConnectionFailuresRef.current = 0;
        })
        .catch(handlePollRejection);
    }, EMAIL_VERIFICATION_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [gated, outcome, refresh, surface]);

  // Tick the clock while a cooldown is active so the countdown re-renders.
  useEffect(() => {
    if (sentAt === undefined) {
      return;
    }
    const interval = setInterval(() => {
      const remaining = remainingCooldownMs(
        sentAt,
        Date.now(),
        EMAIL_VERIFICATION_RESEND_COOLDOWN_MS
      );
      setNow(Date.now());
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1_000);
    return () => clearInterval(interval);
  }, [sentAt]);

  const handleResend = useCallback((): void => {
    // Client cooldown is a UX hint; the server is authoritative (spec §7).
    if (remainingCooldownMs(sentAt, Date.now(), EMAIL_VERIFICATION_RESEND_COOLDOWN_MS) > 0) {
      return;
    }
    setSentAt(Date.now());
    setNow(Date.now());
    void track({
      category: EVENT_CATEGORIES.ONBOARDING,
      name: EVENT_NAMES.VERIFICATION_RESENT,
      properties: { surface }
    });

    void (async () => {
      try {
        const token = await getAccessTokenSilently();
        const idBytes = toIdFromHex(studioUser?.id ?? '');
        if (idBytes && claims.email) {
          // Shared dedupe guard is armed inside resendVerificationEmail (spec §7).
          await resendVerificationEmail({ id: idBytes, token, email: claims.email });
        }
      } catch {
        // A server rejection (e.g. rate limit) is acceptable — the client
        // cooldown still applies and the UI does not break (spec §7).
      }
    })();
  }, [sentAt, surface, getAccessTokenSilently, studioUser, claims.email]);

  const handleRetry = useCallback((): void => {
    setPollFailed(false);
    setResolveExhausted(false);
    unknownFiredRef.current = false;
    // A manual retry starts a fresh connection-failure run (Failure 1).
    consecutiveConnectionFailuresRef.current = 0;
    void refresh().catch((error: unknown) => {
      // Keep the user on the pending/loader path for a recoverable outcome
      // (login_required/consent_required/timeout) instead of bouncing straight
      // back to the error screen (ENG-3797 Failure 1); only a genuine connection
      // failure re-shows it.
      if (classifyRefreshFailure(error) === 'recoverable') {
        return;
      }
      setPollFailed(true);
      void track({
        category: EVENT_CATEGORIES.ONBOARDING,
        name: EVENT_NAMES.VERIFICATION_POLL_FAILED,
        properties: { surface, reason: POLL_FAILURE_REASON }
      });
    });
  }, [refresh, surface]);

  const handleChangeEmail = useCallback(
    async (newEmail: string): Promise<void> => {
      const token = await getAccessTokenSilently();
      const idBytes = toIdFromHex(studioUser?.id ?? '');
      if (idBytes) {
        // Registry update nulls verification server-side (spec §8); log out so
        // the user re-enters the verify loop cleanly with fresh claims.
        await changeUserEmail({ id: idBytes, email: newEmail, token });
      }
      void logout();
    },
    [getAccessTokenSilently, studioUser, logout]
  );

  const handleLogout = useCallback((): void => {
    void logout();
  }, [logout]);

  if (outcome === 'allow') {
    return <>{children}</>;
  }

  if (outcome === 'resolving') {
    return <Loader />;
  }

  if (outcome === 'error') {
    return (
      <VerificationError
        onRetry={handleRetry}
        onResend={handleResend}
        cooldownRemainingMs={cooldownRemaining}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <PendingVerification
      surface={surface}
      email={claims.email}
      cooldownRemainingMs={cooldownRemaining}
      onResend={handleResend}
      onChangeEmail={handleChangeEmail}
      onLogout={handleLogout}
    />
  );
};
